/**
 * CallsChatTab — Doctor Dashboard tab for the Turbo-gated comms layer.
 *
 * Three toggles, all visible regardless of plan, but flipped to disabled
 * with a "Turbo" lock badge until the doctor's tier == "turbo":
 *   1. Voice calls       (calls_enabled)
 *   2. Direct messaging  (chat_enabled)
 *   3. Personal WhatsApp (whatsapp_personal_enabled — defaults ON)
 *
 * Plus a sub-form when calls are routed to a receptionist (call_route ==
 * "receptionist"). We store invite intent only — full app-to-app routing
 * lands when the VoIP infrastructure ships.
 */
import React, { useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Phone, MessageCircle, Lock, Save, Loader2, AlertTriangle, CheckCircle, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import VoiceCallSettings from '@/components/dashboard/VoiceCallSettings';

export default function CallsChatTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [turbo, setTurbo] = useState(false);
  const [tier, setTier] = useState('free');
  const [expiresAt, setExpiresAt] = useState(null);
  const [s, setS] = useState({
    calls_enabled: false,
    call_route: 'doctor',
    receptionist_name: '',
    receptionist_invite_email: '',
    chat_enabled: false,
    whatsapp_personal_enabled: true,
    chat_limits: null,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard/calls-chat');
      setTurbo(!!r.data.turbo_active);
      setTier(r.data.tier || 'free');
      setExpiresAt(r.data.premium_expires_at || null);
      setS({ ...s, ...(r.data.settings || {}) });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true); setErr(''); setOk('');
    try {
      await api.put('/dashboard/calls-chat', s);
      setOk('Saved.');
      setTimeout(() => setOk(''), 2500);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500"><Loader2 className="animate-spin inline mr-2" size={16}/> Loading…</div>;

  const planLabel = tier === 'turbo' ? 'Turbo' : tier === 'premium' ? 'Coffee Premium' : 'Free (Gold)';
  const expiryStr = expiresAt ? new Date(expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
  const isPaid = tier === 'turbo' || tier === 'premium';

  return (
    <div className="space-y-6" data-testid="calls-chat-tab">
      {/* Active-plan banner — celebrates paid status, no upgrade noise */}
      {isPaid ? (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-5 flex items-start gap-3" data-testid="plan-active-banner">
          <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <CheckCircle size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">{planLabel} active</p>
            <h2 className="text-xl font-semibold text-slate-900 mt-0.5" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {tier === 'turbo' ? 'Voice + private chat — fully unlocked' : 'Premium chat unlocked · Voice = Turbo only'}
            </h2>
            <p className="text-sm text-slate-700 mt-1.5 leading-relaxed">
              {tier === 'turbo'
                ? 'Patients can call you directly (number stays private), receptionist forwarding, unlimited post-booking chat — all live.'
                : 'Post-booking chat: 5 messages per side per booking. Voice calls remain on Turbo only.'}
            </p>
            {expiryStr && (
              <p className="text-xs text-emerald-700 font-medium mt-2">Active until <b>{expiryStr}</b></p>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border-2 p-6 bg-gradient-to-br from-amber-50 via-white to-rose-50 border-amber-200">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0">
              <Crown size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest">Turbo · Comms layer</p>
              <h2 className="text-2xl font-semibold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Direct calls + private messaging — no number exposed
              </h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">
                Patients tap once on your profile to call you (or your receptionist) or send a private message — all through the DocSite app, with your phone number never displayed publicly. Available on the <strong>Turbo</strong> plan.
              </p>
              <Link to="/upgrade" className="inline-flex items-center gap-2 mt-4 rounded-full px-5 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold">
                Upgrade to Turbo · ₹999/mo
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Voice Calls — replaced legacy intent-only toggle with full WebRTC settings */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <VoiceCallSettings />
      </div>

      {/* In-App Chat */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="calls-chat-settings-grid">
      <SettingCard
        icon={<MessageCircle size={20} className="text-emerald-600" />}
        title="Private messaging (DocSite In-App Chat)"
        subtitle={
          turbo ? 'Patients tap "Message Dr." on your public profile to chat with you privately. WhatsApp-style UX, your number never exposed. Unlimited messages on Turbo.'
          : tier === 'premium' ? 'Post-booking chat is active for you — patients you have booked can message you (5 messages per side per booking). Pre-booking chat from the public profile requires Turbo.'
          : 'Pre-booking chat from your public profile is locked. Upgrade to Turbo to unlock. (Free plan still gets 3 post-booking messages per side per booking.)'
        }
        locked={!turbo}
      >
        <Toggle label="Enable private messaging on my public page" checked={s.chat_enabled} disabled={!turbo}
                onChange={(v) => setS({ ...s, chat_enabled: v })} testId="chat-enabled-toggle" />
        {tier === 'premium' && !turbo && (
          <div className="mt-3 flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 rounded-lg p-2.5 border border-emerald-200">
            <CheckCircle size={13} className="shrink-0 mt-0.5" />
            <span>Coffee Premium active · post-booking chat already gives you <b>5 messages per side</b> on every booking thread — no extra setup.</span>
          </div>
        )}
        {s.chat_enabled && turbo && (
          <div className="mt-3 flex items-start gap-2 text-[11px] text-emerald-800 bg-emerald-50 rounded-lg p-2.5 border border-emerald-200">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>Beta · Full chat UI with read receipts and image attach is shipping next sprint. Patient message intents are being recorded today so nobody's lost.</span>
          </div>
        )}
      </SettingCard>

      {/* Chat Limits — protect your time */}
      <SettingCard
        icon={<MessageCircle size={20} className="text-rose-600" />}
        title="How many chats per patient?"
        subtitle="By default, patients on our Free plan can have 3 chats with you, Gold plan gets 5, Turbo is unlimited. You can also set your own limit if you want stricter control."
        locked={false}
      >
        <Toggle label="Set my own chat limit (overrides patient plan defaults)"
                checked={!!(s.chat_limits && s.chat_limits.enabled)}
                disabled={false}
                onChange={(v) => setS({
                  ...s,
                  chat_limits: v
                    ? { enabled: true, max_sessions: (s.chat_limits?.max_sessions) || 3, window_days: (s.chat_limits?.window_days) || 365 }
                    : { enabled: false, max_sessions: 3, window_days: 365 }
                })}
                testId="chat-limits-toggle" />
        {s.chat_limits?.enabled && (
          <div className="mt-3 grid grid-cols-2 gap-3" data-testid="chat-limits-block">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Max chat sessions per patient</label>
              <input type="number" min={1} max={999} value={s.chat_limits.max_sessions || 3}
                     onChange={(e) => setS({ ...s, chat_limits: { ...s.chat_limits, max_sessions: parseInt(e.target.value || '1', 10) } })}
                     className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                     data-testid="chat-limits-max-input" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Time window (days)</label>
              <input type="number" min={1} max={3650} value={s.chat_limits.window_days || 365}
                     onChange={(e) => setS({ ...s, chat_limits: { ...s.chat_limits, window_days: parseInt(e.target.value || '1', 10) } })}
                     className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 outline-none"
                     data-testid="chat-limits-window-input" />
            </div>
            <p className="col-span-2 text-[11px] text-slate-500">
              Example: <b>{s.chat_limits.max_sessions || 3}</b> chat sessions per patient in <b>{s.chat_limits.window_days || 365}</b> days. Once a patient hits the limit, they'll see a friendly note to book a visit instead.
            </p>
          </div>
        )}
      </SettingCard>

      {/* WhatsApp privacy */}
      <SettingCard
        icon={<MessageCircle size={20} className="text-green-600" />}
        title="Personal WhatsApp number"
        subtitle={turbo ? 'On Turbo, you can mask your personal WhatsApp and route patients to in-app chat instead.'
                        : tier === 'premium' ? 'Coffee Premium keeps your WhatsApp link visible by default. Turn it off if you prefer in-app messaging only — patients will see Book + Call options.'
                        : 'Patients currently see your raw WhatsApp link. Upgrade to Turbo to mask it and offer private in-app chat instead.'}
        locked={false}
      >
        <Toggle label="Show my personal WhatsApp link to patients (wa.me)"
                checked={s.whatsapp_personal_enabled}
                disabled={false}
                onChange={(v) => setS({ ...s, whatsapp_personal_enabled: v })}
                testId="wa-personal-toggle" />
        {!s.whatsapp_personal_enabled && !turbo && (
          <div className="mt-3 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50 rounded-lg p-2.5 border border-amber-200">
            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
            <span>Heads up: with WhatsApp off and Turbo not active, patients will only see Book + Call options. Most doctors keep this on until they're on Turbo.</span>
          </div>
        )}
      </SettingCard>
      </div>

      {/* Save bar */}
      {err && <div className="rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700" data-testid="calls-chat-err">{err}</div>}
      {ok && <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-800" data-testid="calls-chat-ok">{ok}</div>}
      <div className="flex justify-end">
        <button onClick={save} disabled={saving}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-slate-900 hover:bg-black text-white font-semibold disabled:opacity-50"
                data-testid="calls-chat-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Save settings
        </button>
      </div>
    </div>
  );
}

// ============== UI helpers ==============

function SettingCard({ icon, title, subtitle, locked, children }) {
  return (
    <div className={`rounded-2xl border bg-white p-6 ${locked ? 'border-slate-200 opacity-95' : 'border-slate-200'}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center shrink-0">{icon}</div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
            {locked && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-[10px] font-bold tracking-wider uppercase">
                <Lock size={10} /> Turbo
              </span>
            )}
          </h3>
          <p className="text-sm text-slate-600 mt-1 leading-relaxed">{subtitle}</p>
        </div>
      </div>
      <div className={locked ? 'pointer-events-none opacity-50 select-none' : ''}>
        {children}
      </div>
    </div>
  );
}

function Toggle({ label, checked, onChange, disabled, testId }) {
  return (
    <label className={`flex items-center gap-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <span className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-slate-300'}`}>
        <input type="checkbox" className="sr-only" checked={!!checked} disabled={!!disabled} onChange={(e) => onChange(e.target.checked)} data-testid={testId} />
        <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </span>
      <span className="text-sm text-slate-800 font-medium select-none">{label}</span>
    </label>
  );
}

function RouteOption({ active, title, body, onClick, testId }) {
  return (
    <button onClick={onClick}
            className={`text-left rounded-xl border-2 p-4 transition-all ${active ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white hover:border-slate-400'}`}
            data-testid={testId}>
      <p className="font-semibold text-slate-900 text-sm">{title}</p>
      <p className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</p>
    </button>
  );
}
